// OverviewDashboard — extracted from src/App.jsx (Range A: 4114-4245 + Range B: 4333-5406)
// in Phase 12 strangler-fig refactor. Wave 4 first target. Read-only consumer of 7 tables.
//
// IMPORTANT — non-contiguous extract:
// MonthlyTargetsSection (App.jsx:4248-4330, Phase 2 export used by Settings) sits BETWEEN
// Overview's sub-components в App.jsx. It STAYS in App.jsx — Overview does NOT use it
// (verified via grep, only mentioned в section-header comment between AgentTargetsTable
// and CapCountryChart).
//
// Co-located in this file (all originally siblings in App.jsx, used only by Overview):
//   - BotStatusWidget (was App.jsx:4115-4184)
//   - AgentTargetsTable (was App.jsx:4187-4245)
//   - CapCountryChart (was App.jsx:4333-4381)
//
// Body verbatim from original except:
//   (1) Function signature → `export default function OverviewDashboard` (React.lazy requires
//       default export).
//   (2) BotStatusWidget's 2 direct `sessionToken = null;` mutations (were App.jsx:4123, 4129)
//       → `resetSessionTokenCache();` calls (anti-pattern 5.21 workaround — ES module
//       live-bindings prevent direct module-state mutation from importers; semantics
//       identical, see App.jsx:1014 for the helper, Settings/index.jsx for first introducer).
//
// Inline closures (kept inside OverviewDashboard body, NOT extracted):
//   - KpiCard (was App.jsx:4692, now ~line 263 в this file)
//   - PeriodSelector (was App.jsx:4722, now ~line 293)
//   - AreaChart (was App.jsx:4773, now ~line 344)
//   - RadialProgress (was App.jsx:4882, now ~line 453)
//
// All other behavior, inline styles, comments preserved verbatim.

import React, { useState, useEffect } from 'react';

// utils.js
import { getPersonColor, getPersonTextColor, normalizeAgent, normalizeDealAgent, nowGMT2 } from '../../utils.js';

// constants.js
import { MONTHS } from '../../constants.js';

// App.jsx (Phase 2-9 milestone exports — no new ones этой phase)
import {
  API_BASE,
  BlitzHeader,
  CurrencyBadges,
  apiGet,
  apiSave,
  resetSessionTokenCache,    // Phase 2 helper — replaces direct sessionToken=null
} from '../../App.jsx';


// ── BotStatusWidget: mini bot status card for Overview Dashboard ──
function BotStatusWidget() {
  const [botData, setBotData] = useState(null);
  const [botLoading, setBotLoading] = useState(true);
  const PURPLE = "#A855F7";

  useEffect(() => {
    // 2026-05-15 PR#52 PSC sweep (from PR#51 sub-agent F7):
    // Pre-fix: setInterval polling без AbortController → если unmount mid-fetch,
    // .then(setBotData) runs on unmounted component → React warning + potential
    // stale state if remounted. Plus out-of-order risk если interval fires next
    // iteration before previous fetch completes (slow network).
    // Fix: track latest AbortController + isMounted flag.
    let isMounted = true;
    let currentController = null;
    const load = () => {
      if (currentController) currentController.abort();
      const ctrl = new AbortController();
      currentController = ctrl;
      // v28.13: fresh token — blitz_token first, then blitz_session fallback
      resetSessionTokenCache();
      let tok = localStorage.getItem('blitz_token');
      if (!tok) { try { const s=JSON.parse(localStorage.getItem('blitz_session')||'{}'); tok=s.token||null; } catch {} }
      if (!tok) { if (isMounted) setBotLoading(false); return; }
      fetch(`${API_BASE}/autob2026/status`, { headers: { 'Authorization': `Bearer ${tok}`, 'Content-Type': 'application/json' }, signal: ctrl.signal })
        .then(r => {
          if (r.status === 401) { resetSessionTokenCache(); return null; }
          return r.ok ? r.json() : null;
        })
        .then(d => { if (isMounted) { if (d) setBotData(d); setBotLoading(false); } })
        .catch((e) => { if (isMounted && e?.name !== 'AbortError') setBotLoading(false); });
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { isMounted = false; if (currentController) currentController.abort(); clearInterval(iv); };
  }, []);

  const stats = botData?.stats || null;
  const status = botData?.status || null;
  const actions = botData?.actions || [];

  if (botLoading) return (
    <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 14, padding: "20px 24px", marginBottom: 20 }}>
      <div style={{ color: "#94A3B8", fontSize: 13 }}>Loading bot status...</div>
    </div>
  );

  return (
    <div style={{ background: "#FFFFFF", border: "1px solid rgba(168,85,247,0.2)", borderRadius: 14, padding: "20px 24px", marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 20 }}>🤖</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#0F172A" }}>AUTO-B2026 Bot</div>
            {status && <div style={{ fontSize: 12, color: "#64748B", marginTop: 1 }}>{status.running ? "🟢 Running" : "🔴 Offline"}</div>}
          </div>
        </div>
        {stats && (
          <div style={{ display: "flex", gap: 16, fontSize: 13 }}>
            <span style={{ color: "#64748B" }}>Today: <b style={{ color: "#38BDF8" }}>{stats.today || 0}</b></span>
            <span style={{ color: "#64748B" }}>Total: <b style={{ color: "#F1F5F9" }}>{stats.total || 0}</b></span>
            <span style={{ color: "#64748B" }}>✅ <b style={{ color: "#34D399" }}>{stats.success || 0}</b></span>
          </div>
        )}
      </div>
      {!botData && <div style={{ color: "#94A3B8", fontSize: 13 }}>Bot status unavailable — run <code>python dashboard.py</code></div>}
      {actions.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Recent Actions</div>
          {actions.slice(0, 5).map((a, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 0", fontSize: 12, borderBottom: "1px solid #F1F5F9" }}>
              <span style={{ color: "#94A3B8", fontFamily: "'JetBrains Mono',monospace", minWidth: 55 }}>{(a.time || "").slice(11, 19)}</span>
              <span style={{ padding: "1px 8px", borderRadius: 10, background: "rgba(168,85,247,0.1)", color: PURPLE, fontWeight: 700, fontSize: 11 }}>{a.action || a.type || "action"}</span>
              <span style={{ color: "#475569", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.broker || a.affiliate || a.details || ""}</span>
              <span style={{ color: a.success !== false ? "#10B981" : "#EF4444" }}>{a.success !== false ? "✅" : "❌"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── AgentTargetsTable: Cap targets for Overview Dashboard (receives dcEntries+now as props) ──
function AgentTargetsTable({ dcEntries, now, agentFtd = {}, agentBrandRec = {} }) {
  const targetKey = `blitz_cap_targets_${now.getUTCFullYear()}_${now.getUTCMonth()}`;
  const savedTgt = JSON.parse(localStorage.getItem(targetKey) || "{}");
  // `now` — это nowGMT2(): Date, у которого UTC-ПОЛЯ равны киевскому настенному
  // времени. Читать его локальными getMonth()/getFullYear() значит прибавить
  // смещение зоны второй раз — в последние часы месяца «прошлый месяц» съезжал
  // на один вперёд. Ключ хранилища строкой выше и monthPrefix ниже уже читаются
  // через getUTC*; здесь та же ось.
  const prevMonth = now.getUTCMonth() === 0
    ? { year: now.getUTCFullYear() - 1, month: 11 }
    : { year: now.getUTCFullYear(), month: now.getUTCMonth() - 1 };
  const prevMonthPfx = `${prevMonth.year}-${String(prevMonth.month + 1).padStart(2, "0")}`;
  const prevMonthEntries = dcEntries.filter(d => (d.date || "").startsWith(prevMonthPfx));
  const agentsFromLastMonth = [...new Set(prevMonthEntries.map(d => normalizeAgent((d.agent || "").trim())).filter(Boolean))].sort();
  const savedAgents = Object.keys(savedTgt).filter(k => !k.startsWith("__"));
  const allAgents = [...new Set([...agentsFromLastMonth, ...savedAgents])].sort();
  const initialTargets = { ...savedTgt };
  agentsFromLastMonth.forEach(a => { if (!(a in initialTargets)) initialTargets[a] = ","; });
  const [targets, setTargets] = useState(initialTargets);
  const monthPrefix = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
  const monthDcAll = dcEntries.filter(d => (d.date || "").startsWith(monthPrefix));
  const mAgentAff = {};
  monthDcAll.forEach(d => {
    const a = normalizeAgent((d.agent || "").trim()); if (!a) return;
    mAgentAff[a] = (mAgentAff[a] || 0) + (parseInt(d.affiliates) || 0);
  });
  const agentKeys = [...new Set([...Object.keys(targets).filter(k => !k.startsWith("__")), ...allAgents, ...Object.keys(agentFtd), ...Object.keys(agentBrandRec)])].sort();
  const getVal = (agent, i) => { const p = String(targets[agent] || ",").split(","); return p[i] || ""; };
  if (agentKeys.length === 0) return null;
  return (
    <div style={{ marginTop: 20, background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 14, padding: "16px 20px" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 12 }}>📊 Agent Targets (this month)</div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ background: "#F8FAFC" }}>
            <th style={{ padding: "6px 10px", textAlign: "left", color: "#64748B", fontWeight: 700, fontSize: 11 }}>Agent</th>
            <th style={{ padding: "6px 10px", textAlign: "center", color: "#64748B", fontWeight: 700, fontSize: 11 }}>Aff Target</th>
            <th style={{ padding: "6px 10px", textAlign: "center", color: "#64748B", fontWeight: 700, fontSize: 11 }}>Aff Done</th>
            <th style={{ padding: "6px 10px", textAlign: "center", color: "#64748B", fontWeight: 700, fontSize: 11 }}>Brand Target</th>
            <th style={{ padding: "6px 10px", textAlign: "center", color: "#64748B", fontWeight: 700, fontSize: 11 }}>Brand Done</th>
            <th style={{ padding: "6px 10px", textAlign: "center", color: "#64748B", fontWeight: 700, fontSize: 11 }}>FTDs</th>
          </tr>
        </thead>
        <tbody>
          {agentKeys.map(agent => {
            const affTgt = parseInt(getVal(agent, 0)) || 0;
            const brdTgt = parseInt(getVal(agent, 1)) || 0;
            const affDone = mAgentAff[agent] || 0;
            const brdDone = agentBrandRec[agent] || 0; // факт (received) из CRG, не план — owner 19.08
            const ftdDone = agentFtd[agent] || 0;
            return (
              <tr key={agent} style={{ borderBottom: "1px solid #F1F5F9" }}>
                <td style={{ padding: "6px 10px", fontWeight: 600 }}>{agent}</td>
                <td style={{ padding: "6px 10px", textAlign: "center", color: "#64748B" }}>{affTgt || "—"}</td>
                <td style={{ padding: "6px 10px", textAlign: "center", color: affTgt > 0 && affDone >= affTgt ? "#10B981" : "#0F172A", fontWeight: 700 }}>{affDone}</td>
                <td style={{ padding: "6px 10px", textAlign: "center", color: "#64748B" }}>{brdTgt || "—"}</td>
                <td style={{ padding: "6px 10px", textAlign: "center", color: brdTgt > 0 && brdDone >= brdTgt ? "#10B981" : "#0F172A", fontWeight: 700 }}>{brdDone}</td>
                <td style={{ padding: "6px 10px", textAlign: "center", color: "#0F172A", fontWeight: 700 }}>{ftdDone}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CapCountryChart({ crgDeals }) {
  const countryMap = {};
  (crgDeals||[]).forEach(d => {
    const aff = d.affiliate||""; const m = aff.match(/[A-Z]{2}$/); const c = m ? m[0] : null;
    if (!c) return;
    if (!countryMap[c]) countryMap[c] = { cap:0, received:0 };
    countryMap[c].cap += parseInt(d.cap)||0;
    countryMap[c].received += parseInt(d.capReceived)||0;
  });
  const sorted = Object.entries(countryMap).sort((a,b)=>b[1].cap-a[1].cap).slice(0,6);
  if (!sorted.length) return null;
  const COLORS = ["#6366F1","#10B981","#F59E0B","#EF4444","#A855F7","#0EA5E9"];
  return (
    <div style={{ background:"#fff", border:"1px solid #E2E8F0", borderRadius:14, padding:"16px 20px", marginBottom:20, boxShadow:"0 2px 12px rgba(0,0,0,0.06)" }}>
      <div style={{ fontSize:13, fontWeight:700, color:"#334155", marginBottom:14, display:"flex", alignItems:"center", gap:8 }}>
        &#127775; CAP by Country
        <span style={{ fontSize:11, fontWeight:500, color:"#94A3B8" }}>Top 6 · delivery %</span>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:10 }}>
        {sorted.map(([country, v], i) => {
          const pct = v.cap > 0 ? Math.min(100, Math.round(v.received / v.cap * 100)) : 0;
          const color = COLORS[i];
          const pctColor = pct >= 80 ? "#10B981" : pct >= 50 ? "#F59E0B" : "#EF4444";
          const r = 28, circ = 2 * Math.PI * r;
          const dash = (pct / 100) * circ;
          return (
            <div key={country} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
              <div style={{ position:"relative", width:72, height:72 }}>
                <svg width="72" height="72" style={{ transform:"rotate(-90deg)" }}>
                  <circle cx="36" cy="36" r={r} fill="none" stroke="#F1F5F9" strokeWidth="8" />
                  <circle cx="36" cy="36" r={r} fill="none" stroke={color} strokeWidth="8"
                    strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round"
                    style={{ filter:`drop-shadow(0 0 4px ${color}88)` }} />
                </svg>
                <div style={{ position:"absolute", top:0, left:0, width:72, height:72, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                  <div style={{ fontSize:13, fontWeight:800, color:pctColor, lineHeight:1 }}>{pct}%</div>
                </div>
              </div>
              <div style={{ fontSize:12, fontWeight:800, color:"#334155" }}>{country}</div>
              <div style={{ fontSize:10, color:"#64748B", textAlign:"center", lineHeight:1.3 }}>
                {v.received.toLocaleString()}<span style={{ color:"#94A3B8" }}>/{v.cap.toLocaleString()}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// BlitzRecords — extracted from inline IIFE in OverviewDashboard's JSX (Phase B ESLint fix,
// PR#59). The IIFE pattern violated react-hooks/rules-of-hooks because useState calls inside
// an anonymous IIFE are not recognized as belonging к a proper React function component.
// Behavior unchanged — same JSX output, same localStorage keys (blitz_dashboard_records +
// blitz_reports_all sync), same 4-field structure.
function BlitzRecords() {
  const BR_KEY = "blitz_reports_all";
  const RECORDS_KEY = "blitz_dashboard_records";
  const defaultRecords = {
    dailyFtds: "33", dailyDate: "07/11/2025",
    weekendFtds: "21", weekendDate: "28/02-01/03",
    weeklyFtds: "139", weeklyDate: "06/10-12/10",
    monthlyFtds: "471", monthlyDate: "Oct 2025",
  };
  const loadRecords = () => {
    try {
      const dedicated = JSON.parse(localStorage.getItem(RECORDS_KEY) || 'null');
      if (dedicated) return dedicated;
      const all = JSON.parse(localStorage.getItem(BR_KEY) || '{}');
      const latest = Object.values(all).pop() || {};
      if (latest.dailyFtdsRecord) return {
        dailyFtds: latest.dailyFtdsRecord, dailyDate: latest.dailyFtdsDate || '',
        weekendFtds: latest.weekendFtdsRecord, weekendDate: latest.weekendFtdsDate || '',
        weeklyFtds: latest.weeklyFtdsRecord, weeklyDate: latest.weeklyFtdsDate || '',
        monthlyFtds: latest.monthlyFtdsRecord, monthlyDate: latest.monthlyFtdsDate || '',
      };
    } catch {}
    return defaultRecords;
  };
  const [editingRecords, setEditingRecords] = useState(false);
  const [recs, setRecs] = useState(loadRecords);
  const [editRecs, setEditRecs] = useState(recs);
  const saveRecs = (r) => {
    setRecs(r); setEditingRecords(false);
    try { localStorage.setItem(RECORDS_KEY, JSON.stringify(r)); } catch {}
    // Also sync to blitz-reports
    try {
      const all = JSON.parse(localStorage.getItem(BR_KEY) || '{}');
      // Ключ месяца в blitz-reports — киевский, как и везде в файле (nowGMT2 + getUTC*).
      // `new Date()` с локальными геттерами давал чужой месяц у последних часов суток.
      const curMo = (() => { const n = nowGMT2(); return `${n.getUTCFullYear()}-${String(n.getUTCMonth()+1).padStart(2,'0')}`; })();
      all[curMo] = { ...(all[curMo]||{}),
        dailyFtdsRecord: r.dailyFtds, dailyFtdsDate: r.dailyDate,
        weekendFtdsRecord: r.weekendFtds, weekendFtdsDate: r.weekendDate,
        weeklyFtdsRecord: r.weeklyFtds, weeklyFtdsDate: r.weeklyDate,
        monthlyFtdsRecord: r.monthlyFtds, monthlyFtdsDate: r.monthlyDate,
      };
      localStorage.setItem(BR_KEY, JSON.stringify(all));
    } catch {}
  };
  const fields = [
    { key: 'dailyFtds', dateKey: 'dailyDate', label: 'Daily FTDs', icon: '📅', color: '#6366F1' },
    { key: 'weekendFtds', dateKey: 'weekendDate', label: 'Weekend FTDs', icon: '🌅', color: '#F59E0B' },
    { key: 'weeklyFtds', dateKey: 'weeklyDate', label: 'Weekly FTDs', icon: '📆', color: '#10B981' },
    { key: 'monthlyFtds', dateKey: 'monthlyDate', label: 'Monthly FTDs', icon: '🏆', color: '#EC4899' },
  ];
  return (
    <div style={{ marginBottom: 20, background: "#ffffff", borderRadius: 16, padding: "16px 20px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", border: "1px solid #E2E8F0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18 }}>🏆</span>
          <span style={{ fontSize: 14, fontWeight: 800, color: "#1E293B", letterSpacing: 0.5 }}>BLITZ RECORDS</span>
          <span style={{ fontSize: 10, color: "#10B981", background: "#F0FDF4", padding: "2px 8px", borderRadius: 6, fontWeight: 700, border: "1px solid #D1FAE5" }}>All Time</span>
        </div>
        {!editingRecords
          ? <button onClick={() => { setEditRecs({...recs}); setEditingRecords(true); }} style={{ background: "#F1F5F9", border: "1px solid #E2E8F0", color: "#64748B", padding: "4px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>✏️ Edit</button>
          : <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => saveRecs(editRecs)} style={{ background: "#10B981", border: "none", color: "#FFF", padding: "4px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700 }}>✓ Save</button>
              <button onClick={() => setEditingRecords(false)} style={{ background: "#F1F5F9", border: "1px solid #E2E8F0", color: "#64748B", padding: "4px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12 }}>✕</button>
            </div>
        }
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
        {fields.map(f => (
          <div key={f.key} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 12, padding: "12px 14px", border: `1px solid ${f.color}30`, position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: f.color, borderRadius: "12px 12px 0 0", opacity: 0.8 }} />
            <div style={{ fontSize: 10, color: "#64748B", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6, display: "flex", alignItems: "center", gap: 5 }}>
              <span>{f.icon}</span>{f.label}
            </div>
            {editingRecords ? (
              <div>
                <input value={editRecs[f.key]} onChange={e => setEditRecs(p => ({...p, [f.key]: e.target.value}))}
                  style={{ width: "100%", background: "rgba(255,255,255,0.1)", border: `1px solid ${f.color}60`, borderRadius: 6, padding: "4px 8px", color: "#FFF", fontSize: 20, fontWeight: 800, textAlign: "center", outline: "none", marginBottom: 4, boxSizing: "border-box" }} />
                <input value={editRecs[f.dateKey]} onChange={e => setEditRecs(p => ({...p, [f.dateKey]: e.target.value}))}
                  style={{ width: "100%", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, padding: "3px 6px", color: "#64748B", fontSize: 11, textAlign: "center", outline: "none", boxSizing: "border-box" }} placeholder="date" />
              </div>
            ) : (
              <>
                <div style={{ fontSize: 28, fontWeight: 900, color: f.color, fontFamily: "'JetBrains Mono',monospace", lineHeight: 1.1 }}>{recs[f.key] || "—"}</div>
                <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>{recs[f.dateKey] || ""}</div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function OverviewDashboard({ user, onLogout,onNav, crgDeals: rawOvCrg, dcEntries: rawOvDc, dealsData: rawOvDeals, offersRaw: rawOvOffers, partnersData: rawOvPartners, ftdEntries: rawOvFtd, userAccess }) {
  const crgDeals = Array.isArray(rawOvCrg) ? rawOvCrg : [];
  const dcEntries = Array.isArray(rawOvDc) ? rawOvDc : [];
  const offers = Array.isArray(rawOvDeals) ? rawOvDeals : [];
  const partnersAll = Array.isArray(rawOvPartners) ? rawOvPartners : [];
  const ftdEntries = Array.isArray(rawOvFtd) ? rawOvFtd : [];
  const now = nowGMT2();
  const today = now.toISOString().split("T")[0];

  // ── Period selector state ──
  const [period, setPeriod] = useState(() => localStorage.getItem("blitz_ov_period") || "today");
  const [compare, setCompare] = useState(true);
  const [customRange, setCustomRange] = useState({ from: today, to: today });
  const [periodDropOpen, setPeriodDropOpen] = useState(false);
  const [ftdTarget, setFtdTarget] = useState(() => parseInt(localStorage.getItem("blitz_ftd_target") || "200"));
  const [ftdSuperTarget, setFtdSuperTarget] = useState(() => parseInt(localStorage.getItem("blitz_ftd_super_target") || "300"));
  const [dolphinTarget, setDolphinTarget] = useState(() => parseInt(localStorage.getItem("blitz_dolphin_target") || "50"));
  // Load targets from server on mount so they're shared across devices
  React.useEffect(() => {
    apiGet("daily-summary").then(d => {
      const s = Array.isArray(d) && d[0] ? d[0] : {};
      if (s.ftdTarget) { setFtdTarget(parseInt(s.ftdTarget)); localStorage.setItem("blitz_ftd_target", s.ftdTarget); }
      if (s.ftdSuperTarget) { setFtdSuperTarget(parseInt(s.ftdSuperTarget)); localStorage.setItem("blitz_ftd_super_target", s.ftdSuperTarget); }
      if (s.dolphinTarget) { setDolphinTarget(parseInt(s.dolphinTarget)); localStorage.setItem("blitz_dolphin_target", s.dolphinTarget); }
    }).catch(()=>{});
  }, []);
  const [editingTargets, setEditingTargets] = useState(false);
  const [ftdTargetInput, setFtdTargetInput] = useState("");
  const [ftdSuperTargetInput, setFtdSuperTargetInput] = useState("");
  const [dolphinTargetInput, setDolphinTargetInput] = useState("");

  // ── Date range helpers ──
  // Chrome отдаёт "" для <input type="date">, пока дата набрана не полностью или очищена.
  // Такое значение нельзя превращать в Date: new Date("T00:00:00") невалидна, и
  // toISOString() в getPrevRange бросал RangeError (crash-report 01.09). Пустое/битое → today.
  const validYmd = (s) => (/^\d{4}-\d{2}-\d{2}$/.test(s || "") && !Number.isNaN(new Date(s + "T00:00:00").getTime()) ? s : null);
  const getDateRange = (p) => {
    const d = nowGMT2();
    if (p === "today") return { from: today, to: today, label: "Today", prevLabel: "Yesterday" };
    if (p === "yesterday") { d.setUTCDate(d.getUTCDate() - 1); const y = d.toISOString().split("T")[0]; return { from: y, to: y, label: "Yesterday", prevLabel: "Day before" }; }
    if (p === "week") {
      // `d` — nowGMT2(): UTC-поля равны киевскому времени. getDay() читает поля в поясе
      // БРАУЗЕРА, то есть прибавляет смещение второй раз: в воскресенье после 21:00 по
      // Киеву неделя схлопывалась в один день (начало = сегодня вместо прошлого понедельника).
      const dayOfWeek = d.getUTCDay() || 7;
      const mon = new Date(d); mon.setUTCDate(d.getUTCDate() - dayOfWeek + 1);
      return { from: mon.toISOString().split("T")[0], to: today, label: "This Week", prevLabel: "Last Week" };
    }
    if (p === "month") {
      const first = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-01`;
      return { from: first, to: today, label: "This Month", prevLabel: "Last Month" };
    }
    if (p === "custom") return { from: validYmd(customRange.from) || today, to: validYmd(customRange.to) || today, label: "Custom", prevLabel: "Prev Period" };
    return { from: today, to: today, label: "Today", prevLabel: "Yesterday" };
  };

  const getPrevRange = (range) => {
    // Разбор без суффикса Z шёл в поясе БРАУЗЕРА, а обратно строка бралась через
    // toISOString() (UTC) — в Киеве это давало сдвиг предыдущего периода на сутки
    // (01–07.09 → «24–30.08» вместо «25–31.08»). Держим обе стороны на одной оси.
    const fromDate = new Date(range.from + "T00:00:00Z");
    const toDate = new Date(range.to + "T00:00:00Z");
    // Второй рубеж поверх validYmd: невалидная дата здесь = RangeError на toISOString().
    if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) return { from: today, to: today };
    const span = Math.max(1, Math.round((toDate - fromDate) / 86400000) + 1);
    const prevTo = new Date(fromDate); prevTo.setUTCDate(prevTo.getUTCDate() - 1);
    const prevFrom = new Date(prevTo); prevFrom.setUTCDate(prevFrom.getUTCDate() - span + 1);
    return { from: prevFrom.toISOString().split("T")[0], to: prevTo.toISOString().split("T")[0] };
  };

  const range = getDateRange(period);
  const prevRange = getPrevRange(range);

  // Filter helpers
  const inRange = (dateStr, r) => dateStr >= r.from && dateStr <= r.to;

  // ── CRG DEALS ──
  const curCrg = crgDeals.filter(d => inRange(d.date || "", range));
  const curCap = curCrg.reduce((s, d) => s + (parseInt(d.cap) || 0), 0);
  const curStarted = curCrg.filter(d => d.started).length;
  const curFtd = curCrg.reduce((s, d) => s + (parseInt(d.ftd) || 0), 0);
  const curConv = curStarted > 0 ? ((curFtd / curStarted) * 100).toFixed(1) : "0";

  const prevCrg = crgDeals.filter(d => inRange(d.date || "", prevRange));
  const prevCapVal = prevCrg.reduce((s, d) => s + (parseInt(d.cap) || 0), 0);
  const prevStartedVal = prevCrg.filter(d => d.started).length;
  const prevFtdVal = prevCrg.reduce((s, d) => s + (parseInt(d.ftd) || 0), 0);

  // Extract country from CRG affiliate field (e.g. "33 AU" → "AU")
  const extractCountry = (aff) => { const m = (aff || "").match(/[A-Z]{2,4}$/); return m ? m[0] : "??"; };

  // Top 5 countries — always monthly (independent of period selector)
  const _monthPfxCap = (() => { const d = nowGMT2(); return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}`; })();
  const _monthlyCrgForCap = crgDeals.filter(d => (d.date||"").startsWith(_monthPfxCap));
  const crgCountryMap = {};
  _monthlyCrgForCap.forEach(d => { const c = extractCountry(d.affiliate); crgCountryMap[c] = (crgCountryMap[c] || 0) + 1; });
  const topCrgCountries = Object.entries(crgCountryMap).sort((a, b) => b[1] - a[1]).slice(0, 5);

  // Top brokers — always monthly
  const brokerCapMap = {};
  _monthlyCrgForCap.forEach(d => { const b = (d.brokerCap || "").trim(); if (b) brokerCapMap[b] = (brokerCapMap[b] || 0) + (parseInt(d.cap) || 0); });
  const topBrokers = Object.entries(brokerCapMap).sort((a, b) => b[1] - a[1]).slice(0, 5);

  // Top affiliates — always monthly
  const affCapMap = {};
  _monthlyCrgForCap.forEach(d => { const a = (d.affiliate || "").trim(); if (a) affCapMap[a] = (affCapMap[a] || 0) + (parseInt(d.cap) || 0); });
  const topAffCap = Object.entries(affCapMap).sort((a, b) => b[1] - a[1]).slice(0, 5);

  // ── FTD ENTRIES ANALYTICS ──
  // Monthly range — always current month, regardless of period selector
  const monthFirst = (() => { const d = nowGMT2(); return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}-01`; })();
  const monthRange = { from: monthFirst, to: today };
  const curFtdEntries = ftdEntries.filter(d => inRange(d.date || "", range));
  const monthFtdEntries = ftdEntries.filter(d => inRange(d.date || "", monthRange));
  const prevFtdEntries = ftdEntries.filter(d => inRange(d.date || "", prevRange));
  const totalFtdCount = curFtdEntries.reduce((s, d) => s + (parseInt(d.ftd) || 0), 0);
  const prevFtdCount = prevFtdEntries.reduce((s, d) => s + (parseInt(d.ftd) || 0), 0);
  const totalFtdCap = curFtdEntries.reduce((s, d) => s + (parseInt(d.cap) || 0), 0);
  const totalFtdCapReceived = curFtdEntries.reduce((s, d) => s + (parseInt(d.capReceived) || 0), 0);
  // Top 3 affiliates by FTD count — MONTHLY (always current month)
  // v13.03: Use manageAff (human-readable name) as display key; fall back to affiliate field
  const ftdAffMap = {};
  monthFtdEntries.forEach(d => {
    // Leadgreed entries have manageAff = sourceName (readable). Manual entries use affiliate.
    const displayName = (d.manageAff || "").trim() || (d.affiliate || "").trim();
    if (displayName) ftdAffMap[displayName] = (ftdAffMap[displayName] || 0) + (parseInt(d.ftd) || 0);
  });
  const topFtdAff = Object.entries(ftdAffMap).sort((a, b) => b[1] - a[1]).slice(0, 3);
  // Top 3 brokers/brands by FTD count — MONTHLY
  // v13.03: Use destName (readable broker name) as display key; fall back to brokerCap field
  const ftdBrokerMap = {};
  monthFtdEntries.forEach(d => {
    // Leadgreed entries have destName. Manual entries use brokerCap.
    // Strip leading numeric ID from brokerCap (e.g. "3342 Monstrack OPEN" → "Monstrack OPEN")
    const rawBroker = (d.destName || d.brokerName || "").trim() ||
      (d.brokerCap || "").trim().replace(/^\d+\s*[-]?\s*/, "");
    if (rawBroker) ftdBrokerMap[rawBroker] = (ftdBrokerMap[rawBroker] || 0) + (parseInt(d.ftd) || 0);
  });
  const topFtdBrokers = Object.entries(ftdBrokerMap).sort((a, b) => b[1] - a[1]).slice(0, 3);
  // Top 3 countries by FTD count — MONTHLY
  // v13.03: Prefer d.country field (set by Leadgreed); fall back to extractCountry from affiliate
  const ftdCountryMap = {};
  monthFtdEntries.forEach(d => {
    const c = (d.country || "").trim() || extractCountry(d.affiliate || "");
    if (c && c !== "??") ftdCountryMap[c] = (ftdCountryMap[c] || 0) + (parseInt(d.ftd) || 0);
  });
  const topFtdCountries = Object.entries(ftdCountryMap).sort((a, b) => b[1] - a[1]).slice(0, 3);
  // FTD conversion rate (ftd / cap received)
  const ftdConvRate = totalFtdCapReceived > 0 ? ((totalFtdCount / totalFtdCapReceived) * 100).toFixed(1) : "0";
  // Today's FTD entries
  const todayFtdEntries = ftdEntries.filter(d => d.date === today);
  const todayFtdCount = todayFtdEntries.reduce((s, d) => s + (parseInt(d.ftd) || 0), 0);
  const todayFtdCap = todayFtdEntries.reduce((s, d) => s + (parseInt(d.cap) || 0), 0);
  // FTD daily trend for chart — computed after chartDays is defined below

  // ── AGENTS — always monthly ──
  const curDc = dcEntries.filter(d => inRange(d.date || "", range));
  const _monthlyDc = dcEntries.filter(d => (d.date || "").startsWith(_monthPfxCap));
  const agentAff = {}, agentBrand = {}, agentFtd = {}, agentBrandRec = {};
  _monthlyDc.forEach(d => {
    const agent = normalizeAgent((d.agent || "").trim());
    if (!agent) return;
    agentAff[agent] = (agentAff[agent] || 0) + (parseInt(d.affiliates) || 0);
    agentBrand[agent] = (agentBrand[agent] || 0) + (parseInt(d.brands) || 0);
  });
  // agentBrandRec — ФАКТ полученного capReceived по madeSale (owner 19.08: карточка Brands и
  // Brand Done показывают реально принесённое, не плановый cap). Без скипа cap=0 — как agentFtd.
  // agentBrandPlan — плановый cap тех же сделок: owner 20.08 попросил в карточке «факт / план».
  const agentBrandPlan = {};
  _monthlyCrgForCap.forEach(d => {
    const agent = normalizeDealAgent(d.madeSale);
    if (!agent) return;
    agentFtd[agent] = (agentFtd[agent] || 0) + (parseInt(d.ftd) || 0);
    agentBrandRec[agent] = (agentBrandRec[agent] || 0) + (parseInt(d.capReceived) || 0);
    agentBrandPlan[agent] = (agentBrandPlan[agent] || 0) + (parseInt(d.cap) || 0);
  });
  const agentList = [...new Set([...Object.keys(agentAff), ...Object.keys(agentBrand)])].sort();
  const ftdAgentList = [...new Set([...agentList, ...Object.keys(agentFtd)])].sort();
  const brandAgentList = [...new Set([...agentList, ...Object.keys(agentBrandRec)])].sort();

  // Agent Total (Affiliates + Brands from Daily Cap) — monthly
  const agentCap = {};
  _monthlyDc.forEach(d => {
    const a = normalizeAgent((d.agent || "").trim());
    if (!a) return;
    const aff = parseInt(d.affiliates) || 0;
    const brands = parseInt(d.brands) || 0;
    agentCap[a] = (agentCap[a] || 0) + aff + brands;
  });
  const topAgents = Object.entries(agentCap).sort((a, b) => b[1] - a[1]).slice(0, 5);

  // ── OFFERS ──
  const offerCountryMap = {};
  offers.forEach(d => { const c = (d.country || "").trim().toUpperCase(); if (c) offerCountryMap[c] = (offerCountryMap[c] || 0) + 1; });
  const topOfferCountries = Object.entries(offerCountryMap).sort((a, b) => b[1] - a[1]).slice(0, 8);

  // ── CHART DATA — daily breakdown for the period ──
  const getDaysArray = (from, to) => {
    // UTC YMD — иначе toISOString в GMT+2/+3 сдвигает календарный день.
    const days = [];
    const [y1, m1, d1] = from.split("-").map(Number);
    const [y2, m2, d2] = to.split("-").map(Number);
    const end = Date.UTC(y2, m2 - 1, d2);
    for (let t = Date.UTC(y1, m1 - 1, d1); t <= end; t += 86400000) {
      days.push(new Date(t).toISOString().split("T")[0]);
    }
    return days;
  };
  const chartDays = getDaysArray(range.from, range.to);
  const prevDays = getDaysArray(prevRange.from, prevRange.to);
  const ftdChartData = chartDays.map((date) => {
    const dayFtd = ftdEntries.filter(d => d.date === date);
    return { date: date.slice(5), ftd: dayFtd.reduce((s, d) => s + (parseInt(d.ftd) || 0), 0) };
  });

  const chartData = chartDays.map((date, i) => {
    const dayCrg = crgDeals.filter(d => d.date === date);
    const prevDate = prevDays[i] || "";
    const prevDayCrg = crgDeals.filter(d => d.date === prevDate);
    return {
      date: date.slice(5), // MM-DD
      fullDate: date,
      cap: dayCrg.reduce((s, d) => s + (parseInt(d.cap) || 0), 0),
      ftd: dayCrg.reduce((s, d) => s + (parseInt(d.ftd) || 0), 0),
      deals: dayCrg.length,
      prevCap: prevDayCrg.reduce((s, d) => s + (parseInt(d.cap) || 0), 0),
      prevFtd: prevDayCrg.reduce((s, d) => s + (parseInt(d.ftd) || 0), 0),
      prevDeals: prevDayCrg.length,
    };
  });

  // Chart metric selector
  const [chartMetric, setChartMetric] = useState("cap"); // cap | ftd | deals | paid

  // ── Comparison % helper ──
  const pctChange = (cur, prev) => {
    if (prev === 0 && cur === 0) return null;
    if (prev === 0) return { pct: 100, up: true };
    const change = ((cur - prev) / prev) * 100;
    return { pct: Math.abs(change).toFixed(1), up: change >= 0 };
  };

  // ── Styles ──
  const cardStyle = { background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 16, padding: "20px 24px", boxShadow: "0 2px 16px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)" };
  const sectionTitle = (icon, title) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
      <span style={{ fontSize: 18 }}>{icon}</span>
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "#334155" }}>{title}</h3>
    </div>
  );

  const rankedBar = (entries, maxVal, color, unit) => entries.map(([name, val], i) => (
    <div key={name} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: i === 0 ? "#F59E0B" : "#64748B", width: 16 }}>{i + 1}.</span>
      <span style={{ fontSize: 11, fontWeight: 700, color: "#334155", minWidth: 70, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={name}>{name}</span>
      <div style={{ flex: 1, height: 6, background: "#F1F5F9", borderRadius: 5, overflow: "hidden" }}>
        <div style={{ height: "100%", width: (maxVal > 0 ? (val / maxVal) * 100 : 0) + "%", background: i === 0 ? color : "#CBD5E1", borderRadius: 5, transition: "width 0.4s" }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: "#334155", minWidth: 45, textAlign: "right" }}>{val.toLocaleString()}{unit ? " " + unit : ""}</span>
    </div>
  ));

  // KPI card with comparison
  const KpiCard = ({ label, value, prevValue, icon, accent, bg, onClick, prefix, suffix }) => {
    const ch = compare ? pctChange(typeof value === "string" ? parseFloat(value) || 0 : value, prevValue || 0) : null;
    return (
      <div onClick={onClick} style={{ background: bg, border: "1px solid #E2E8F0", borderRadius: 14, padding: "16px 18px", position: "relative", overflow: "hidden", cursor: onClick ? "pointer" : "default", transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
        onMouseEnter={e => { if (onClick) { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = `0 8px 28px rgba(0,0,0,0.1), 0 0 0 2px ${accent}22`; } }}
        onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)"; }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${accent}, ${accent}88)`, borderRadius: "14px 14px 0 0" }} />
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 10, color: "#64748B", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: accent }}>{prefix || ""}{typeof value === "number" ? value.toLocaleString() : value}{suffix || ""}</div>
            {compare && ch !== null && (
              <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: ch.up ? "#10B981" : "#EF4444" }}>
                  {ch.up ? "↑" : "↓"} {ch.pct}%
                </span>
                <span style={{ fontSize: 10, color: "#94A3B8" }}>vs {range.prevLabel.toLowerCase()}</span>
              </div>
            )}
            {compare && ch === null && prevValue !== undefined && (
              <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 4 }}>Prev: {prefix || ""}{(prevValue || 0).toLocaleString()}{suffix || ""}</div>
            )}
          </div>
          <span style={{ fontSize: 24 }}>{icon}</span>
        </div>
      </div>
    );
  };

  // ── Period selector dropdown ──
  const PeriodSelector = () => (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button onClick={() => setPeriodDropOpen(!periodDropOpen)}
        style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", borderRadius: 10, background: "#FFF", border: "2px solid #6366F1", cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#6366F1" }}>
        <span>{range.label}</span>
        <span style={{ fontSize: 10 }}>▼</span>
      </button>
      {periodDropOpen && (
        <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, background: "#FFF", border: "1px solid #E2E8F0", borderRadius: 12, boxShadow: "0 8px 30px rgba(0,0,0,0.12)", zIndex: 100, minWidth: 220, overflow: "hidden" }}>
          {[
            { key: "today", label: "Today", icon: "📅" },
            { key: "yesterday", label: "Yesterday", icon: "⏪" },
            { key: "week", label: "This Week", icon: "📆" },
            { key: "month", label: "This Month", icon: "🗓️" },
          ].map(opt => (
            <div key={opt.key} onClick={() => { setPeriod(opt.key); setPeriodDropOpen(false); }}
              style={{ padding: "10px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, background: period === opt.key ? "#EEF2FF" : "transparent", borderLeft: period === opt.key ? "3px solid #6366F1" : "3px solid transparent", transition: "all 0.15s" }}
              onMouseEnter={e => { if (period !== opt.key) e.currentTarget.style.background = "#F8FAFC"; }}
              onMouseLeave={e => { if (period !== opt.key) e.currentTarget.style.background = "transparent"; }}>
              <span>{opt.icon}</span>
              <span style={{ fontSize: 13, fontWeight: period === opt.key ? 700 : 500, color: period === opt.key ? "#6366F1" : "#334155" }}>{opt.label}</span>
              {period === opt.key && <span style={{ marginLeft: "auto", color: "#6366F1", fontSize: 14 }}>✓</span>}
            </div>
          ))}
          <div style={{ borderTop: "1px solid #E2E8F0", padding: "10px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
              onClick={() => setCompare(!compare)}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Compare to previous period</span>
              <div style={{ width: 36, height: 20, borderRadius: 10, background: compare ? "#6366F1" : "#CBD5E1", padding: 2, transition: "background 0.2s", cursor: "pointer" }}>
                <div style={{ width: 16, height: 16, borderRadius: 8, background: "#FFF", transform: compare ? "translateX(16px)" : "translateX(0)", transition: "transform 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
              </div>
            </div>
          </div>
          <div style={{ borderTop: "1px solid #E2E8F0", padding: "10px 16px" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", marginBottom: 6 }}>Custom Range</div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input type="date" value={customRange.from} onChange={e => setCustomRange(p => ({ ...p, from: e.target.value }))}
                style={{ flex: 1, padding: "4px 6px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 11 }} />
              <span style={{ color: "#94A3B8", fontSize: 11 }}>→</span>
              <input type="date" value={customRange.to} onChange={e => setCustomRange(p => ({ ...p, to: e.target.value }))}
                style={{ flex: 1, padding: "4px 6px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 11 }} />
            </div>
            <button onClick={() => { setPeriod("custom"); localStorage.setItem("blitz_ov_period","custom"); setPeriodDropOpen(false); }}
              style={{ marginTop: 6, width: "100%", padding: "6px", borderRadius: 6, background: "#6366F1", border: "none", color: "#FFF", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>Apply</button>
          </div>
        </div>
      )}
    </div>
  );

  // ── Area Chart Component ──
  const AreaChart = ({ data, currentKey, prevKey, color, prevColor, height, showLabels }) => {
    if (!data || data.length === 0) return null;
    const vals = data.map(d => d[currentKey] || 0);
    const prevVals = compare ? data.map(d => d[prevKey] || 0) : [];
    const allVals = [...vals, ...prevVals];
    const maxVal = Math.max(...allVals, 1);
    const w = 100;
    const h = height || 120;
    const padX = 0; const padY = 5;
    const plotW = w; const plotH = h - padY * 2;

    const toPoint = (arr, i) => {
      const x = arr.length > 1 ? (i / (arr.length - 1)) * plotW : plotW / 2;
      const y = padY + plotH - (arr[i] / maxVal) * plotH;
      return `${x},${y}`;
    };

    const makePath = (arr) => arr.map((_, i) => toPoint(arr, i)).join(" ");
    const makeArea = (arr) => {
      const points = arr.map((_, i) => toPoint(arr, i));
      return `${padX},${h - padY} ${points.join(" ")} ${plotW},${h - padY}`;
    };

    return (
      <div style={{ position: "relative", width: "100%", height: h }}>
        <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: "100%", height: "100%" }}>
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map(pct => (
            <line key={pct} x1={0} x2={w} y1={padY + plotH * (1 - pct)} y2={padY + plotH * (1 - pct)} stroke="#F1F5F9" strokeWidth="0.3" />
          ))}
          {/* Previous period area */}
          {compare && prevVals.length > 0 && (
            <>
              <polygon points={makeArea(prevVals)} fill={prevColor || "#FDE68A"} opacity="0.4" />
              <polyline points={makePath(prevVals)} fill="none" stroke={prevColor || "#F59E0B"} strokeWidth="0.5" opacity="0.6" />
            </>
          )}
          {/* Current period area */}
          <polygon points={makeArea(vals)} fill={color || "#818CF8"} opacity="0.3" />
          <polyline points={makePath(vals)} fill="none" stroke={color || "#6366F1"} strokeWidth="0.7" />
          {/* Data points */}
          {vals.map((v, i) => (
            <circle key={i} cx={parseFloat(toPoint(vals, i).split(",")[0])} cy={parseFloat(toPoint(vals, i).split(",")[1])} r="0.8" fill={color || "#6366F1"} />
          ))}
        </svg>
        {/* X-axis labels */}
        {showLabels !== false && data.length <= 31 && (
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
            {data.filter((_, i) => data.length <= 7 || i % Math.ceil(data.length / 7) === 0 || i === data.length - 1).map((d, i) => (
              <span key={i} style={{ fontSize: 9, color: "#94A3B8" }}>{d.date}</span>
            ))}
          </div>
        )}
      </div>
    );
  };

  const metricOptions = [
    { key: "cap", label: "CAP", color: "#6366F1", prev: "prevCap" },
    { key: "ftd", label: "FTD", color: "#F59E0B", prev: "prevFtd" },
    { key: "deals", label: "Deals", color: "#10B981", prev: "prevDeals" },
    { key: "paid", label: "Paid $", color: "#0EA5E9", prev: "prevPaid" },
  ];
  const activeMetric = metricOptions.find(m => m.key === chartMetric) || metricOptions[0];

  return (
    <div style={{ minHeight: "100vh", background: "#FFFFFF", fontFamily: "'Plus Jakarta Sans','Segoe UI',sans-serif", color: "#0F172A" }}>
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet" />
      {/* BlitzHeader рендерится в App.jsx (постоянная шапка) */}

      <main className="blitz-main" style={{ maxWidth: 1760, margin: "0 auto", padding: "clamp(12px, 3vw, 28px) clamp(12px, 4vw, 32px)" }}>
        {/* ═══ Header + Period Selector ═══ */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#0F172A" }}>📊 Dashboard</h2>
          <CurrencyBadges />
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {compare && <span style={{ fontSize: 11, color: "#94A3B8", background: "#F8FAFC", padding: "4px 10px", borderRadius: 6, border: "1px solid #E2E8F0" }}>vs {range.prevLabel} ({prevRange.from.slice(5)} → {prevRange.to.slice(5)})</span>}
            <PeriodSelector />
          </div>
        </div>


        {/* ═══════════ FIX 9.20: PERFORMANCE OVERVIEW ═══════════ */}
        {(() => {
          const FTD_TARGET = ftdTarget;
          const FTD_SUPER_TARGET = ftdSuperTarget;
          const monthCrg = crgDeals.filter(d => {
            if (!d.date) return false;
            return d.date.startsWith(`${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`);
          });
          const monthFtd = monthFtdEntries.reduce((s, d) => s + (parseInt(d.ftd) || 1), 0);
          const ftdPct   = Math.min(100, FTD_TARGET > 0 ? (monthFtd / FTD_TARGET) * 100 : 0);
          const superPct = Math.min(100, FTD_SUPER_TARGET > 0 ? (monthFtd / FTD_SUPER_TARGET) * 100 : 0);
          // Dolphins: affiliates whose name contains 🐬
          const dolphinFtd = monthFtdEntries.filter(d => (d.affiliateName || d.sourceName || d.manageAff || "").includes("🐬")).reduce((s, d) => s + (parseInt(d.ftd) || 1), 0);
          // Monthly CAP (brands) vs Received
          const _ovMPfx = `${now.getUTCFullYear()}-${String(now.getUTCMonth()+1).padStart(2,"0")}`;
          const _ovMonthDc = dcEntries.filter(d => (d.date||"").startsWith(_ovMPfx));
          const _ovMonthCrg = crgDeals.filter(d => (d.date||"").startsWith(_ovMPfx));
          const _ovMonthCap = _ovMonthCrg.reduce((s,d) => s+(parseInt(d.cap)||0), 0);
          const _ovMonthReceived = _ovMonthCrg.reduce((s,d) => s+(parseInt(d.capReceived)||0), 0);
          const _capRecvPct = _ovMonthCap > 0 ? Math.min(100, (_ovMonthReceived / _ovMonthCap) * 100) : 0;
          const todayCrg = crgDeals.filter(d => d.date === today);
          const todayCap = todayCrg.reduce((s, d) => s + (parseInt(d.cap) || 0), 0);
          const todayCapReceived = todayCrg.reduce((s, d) => s + (parseInt(d.capReceived) || 0), 0);
          const dailyLimit = todayCap || 1;
          const capPct = Math.min(100, dailyLimit > 0 ? (todayCapReceived / dailyLimit) * 100 : 0);
          const getColor   = pct => pct < 30 ? "#EF4444" : pct < 75 ? "#F59E0B" : "#10B981";
          const getColorBg = pct => pct < 30 ? "rgba(239,68,68,0.1)" : pct < 75 ? "rgba(245,158,11,0.1)" : "rgba(16,185,129,0.1)";
          const RadialProgress = ({ pct, color, bg, label, value, total, size = 120 }) => {
            const r = (size - 12) / 2;
            const circ = 2 * Math.PI * r;
            const offset = circ - (circ * Math.min(pct, 100)) / 100;
            return (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
                  <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={bg} strokeWidth={10} />
                  <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={10}
                    strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
                    style={{ transition: "stroke-dashoffset 0.8s ease" }} />
                </svg>
                <div style={{ position: "relative", marginTop: -size + 10, height: size - 20, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ fontSize: 24, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color }}>{Math.round(pct)}%</div>
                  <div style={{ fontSize: 10, color: "#64748B", marginTop: 2 }}>{value} / {total}</div>
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#334155", textAlign: "center", marginTop: 4 }}>{label}</div>
              </div>
            );
          };
          return (
            <div style={{ background: "#FFF", border: "1px solid #E2E8F0", borderRadius: 16, padding: "20px 24px", marginBottom: 28, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                <span style={{ fontSize: 18 }}>🎯</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>Performance Overview</span>
                <span style={{ fontSize: 11, color: "#94A3B8", fontWeight: 500, marginLeft: "auto" }}>{MONTHS[now.getUTCMonth()]} {now.getUTCFullYear()}</span>
                <button onClick={() => {
                    if (!editingTargets) { setFtdTargetInput(String(ftdTarget)); setFtdSuperTargetInput(String(ftdSuperTarget)); setDolphinTargetInput(String(dolphinTarget)); }
                    else {
                      const t = parseInt(ftdTargetInput) || ftdTarget;
                      const s = parseInt(ftdSuperTargetInput) || ftdSuperTarget;
                      const dl = parseInt(dolphinTargetInput) || dolphinTarget;
                      setFtdTarget(t); setFtdSuperTarget(s); setDolphinTarget(dl);
                      localStorage.setItem("blitz_ftd_target", t);
                      localStorage.setItem("blitz_ftd_super_target", s);
                      localStorage.setItem("blitz_dolphin_target", dl);
                      apiSave("daily-summary", [{ ftdTarget: t, ftdSuperTarget: s, dolphinTarget: dl }], user?.email);
                    }
                    setEditingTargets(v => !v);
                  }}
                  style={{ padding:"4px 12px", borderRadius:6, border:"1px solid #E2E8F0", background: editingTargets ? "#ECFDF5" : "#F8FAFC", color: editingTargets ? "#059669" : "#64748B", fontSize:11, fontWeight:700, cursor:"pointer" }}>
                  {editingTargets ? "✅ Save" : "⚙️ Set Targets"}
                </button>
              </div>
              {editingTargets && (
                <div style={{ display:"flex", gap:16, marginBottom:20, padding:"14px 16px", background:"#F8FAFC", borderRadius:10, border:"1px solid #E2E8F0", flexWrap:"wrap" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:12, fontWeight:700, color:"#334155" }}>🎯 FTD Target</span>
                    <input type="number" value={ftdTargetInput} onChange={e=>setFtdTargetInput(e.target.value)}
                      style={{ width:80, padding:"5px 8px", border:"1px solid #6366F1", borderRadius:6, fontSize:14, fontWeight:800, color:"#6366F1", textAlign:"center", outline:"none" }} />
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:12, fontWeight:700, color:"#334155" }}>🚀 Super Target</span>
                    <input type="number" value={ftdSuperTargetInput} onChange={e=>setFtdSuperTargetInput(e.target.value)}
                      style={{ width:80, padding:"5px 8px", border:"1px solid #F59E0B", borderRadius:6, fontSize:14, fontWeight:800, color:"#F59E0B", textAlign:"center", outline:"none" }} />
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:12, fontWeight:700, color:"#334155" }}>🐬 Dolphins Target</span>
                    <input type="number" value={dolphinTargetInput} onChange={e=>setDolphinTargetInput(e.target.value)}
                      style={{ width:80, padding:"5px 8px", border:"1px solid #5B9BD5", borderRadius:6, fontSize:14, fontWeight:800, color:"#5B9BD5", textAlign:"center", outline:"none" }} />
                  </div>
                </div>
              )}
              {/* Radial charts */}
              <div style={{ display: "flex", justifyContent: "center", gap: 40, flexWrap: "wrap", marginBottom: 24 }}>
                <RadialProgress pct={ftdPct} color={getColor(ftdPct)} bg={getColorBg(ftdPct)} label="🎯 FTD Target" value={monthFtd} total={FTD_TARGET} size={140} />
                <RadialProgress pct={superPct} color={getColor(superPct)} bg={getColorBg(superPct)} label="🚀 Super Target" value={monthFtd} total={FTD_SUPER_TARGET} size={140} />
                <RadialProgress pct={capPct} color={getColor(capPct)} bg={getColorBg(capPct)} label="Daily CAP Usage" value={todayCapReceived} total={todayCap || 0} size={140} />
                <RadialProgress pct={Math.min(100, dolphinTarget > 0 ? (dolphinFtd / dolphinTarget) * 100 : 0)} color="#5B9BD5" bg="#EFF6FF" label="🐬 Dolphins" value={dolphinFtd} total={dolphinTarget} size={140} />
                <RadialProgress pct={_capRecvPct} color="#F59E0B" bg="rgba(245,158,11,0.1)" label="💰 CAP Received" value={_ovMonthReceived} total={_ovMonthCap} size={140} />
              </div>
              {/* FTD counters */}
              <div style={{ display:"flex", gap:10, justifyContent:"center", marginBottom:20, flexWrap:"wrap" }}>
                {[
                  { label:"Left for Target", val: Math.max(0, FTD_TARGET - monthFtd), color:"#6366F1", bg:"#EEF2FF", icon:"🎯", sub:`of ${FTD_TARGET}` },
                  { label:"Left for Super", val: Math.max(0, FTD_SUPER_TARGET - monthFtd), color:"#F59E0B", bg:"#FFFBEB", icon:"🚀", sub:`of ${FTD_SUPER_TARGET}` },
                  { label:"FTDs This Month", val: monthFtd, color:"#10B981", bg:"#ECFDF5", icon:"✅", sub:"total" },
                ].map((item, i) => (
                  <div key={i} style={{ background:item.bg, borderRadius:10, padding:"10px 20px", textAlign:"center", minWidth:130 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:0.5, marginBottom:4 }}>{item.icon} {item.label}</div>
                    <div style={{ fontSize:28, fontWeight:900, color:item.color, fontFamily:"'JetBrains Mono',monospace", lineHeight:1 }}>{item.val}</div>
                    <div style={{ fontSize:10, color:"#94A3B8", marginTop:3 }}>{item.sub}</div>
                  </div>
                ))}
              </div>
              {/* Monthly KPI pills */}
              {(() => {
                const _now2 = nowGMT2();
                const _mPfx = `${_now2.getUTCFullYear()}-${String(_now2.getUTCMonth()+1).padStart(2,"0")}`;
                const _monthFrom = `${_mPfx}-01`;
                // Daily-cap Brands only
                const _monthDc = dcEntries.filter(d => (d.date||"").startsWith(_mPfx));
                const _monthBrands = _monthDc.reduce((s,d) => s+(parseInt(d.brands)||0), 0);
                const _daysWithBrands = new Set(_monthDc.filter(d => parseInt(d.brands)>0).map(d=>d.date)).size;
                const _avgDcCap = _daysWithBrands > 0 ? Math.round(_monthBrands / _daysWithBrands) : 0;
                // Deals this month — always monthly, independent of period selector
                const _monthCrg = crgDeals.filter(d => (d.date||"") >= _monthFrom && (d.date||"") <= today);
                // Total Offers — offers are merged into dealsData, identified by having a 'price' field (no per-month date available)
                const _rawDealsAll = Array.isArray(rawOvDeals) ? rawOvDeals : [];
                const _offersThisMonth = _rawDealsAll.filter(o => o.price || o.source).length;
                // Partners this month
                const _rawPartners = Array.isArray(rawOvPartners) ? rawOvPartners : [];
                const _partnersThisMonth = _rawPartners.filter(p => (p.date||"").startsWith(_mPfx)).length;
                return (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:10, marginBottom:20 }}>
                {[
                  { icon:"🛒", label:"Total Offers", val: _offersThisMonth, color:"#6366F1" },
                  { icon:"🤝", label:"Partners This Month", val: _partnersThisMonth, color:"#EC4899" },
                  { icon:"📋", label:"Deals This Month", val: _monthCrg.length, color:"#0EA5E9" },
                  { icon:"📊", label:"CAP This Month", val: _monthBrands, color:"#8B5CF6" },
                  { icon:"📈", label:"Avg CAP This Month", val: _avgDcCap + "/day", color:"#F59E0B" },
                ].map((k,i) => (
                  <div key={i} style={{ background:"#F8FAFC", border:"1px solid #E2E8F0", borderRadius:10, padding:"10px 14px", textAlign:"center" }}>
                    <div style={{ fontSize:18, marginBottom:4 }}>{k.icon}</div>
                    <div style={{ fontSize:11, color:"#94A3B8", fontWeight:600, textTransform:"uppercase", letterSpacing:0.5, marginBottom:4 }}>{k.label}</div>
                    <div style={{ fontSize:20, fontWeight:800, color:k.color, fontFamily:"'JetBrains Mono',monospace" }}>{k.val}</div>
                  </div>
                ))}
              </div>
              );
              })()}
              {/* Top tables */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14 }}>
                <div style={{ background:"#F8FAFC", border:"1px solid #E2E8F0", borderRadius:10, padding:"12px 14px" }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#334155", marginBottom:10 }}>🏆 Top Affiliates <span style={{ fontSize:10, color:"#94A3B8" }}>by FTD</span></div>
                  {topFtdAff.length === 0 ? <div style={{ color:"#94A3B8", fontSize:12 }}>No data</div> :
                    topFtdAff.map(([name, ftd], i) => (
                      <div key={i} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:7 }}>
                        <span style={{ fontSize:11, color:"#94A3B8", minWidth:16 }}>{i+1}</span>
                        <div style={{ flex:1, height:6, background:"#E2E8F0", borderRadius:3, overflow:"hidden" }}>
                          <div style={{ height:"100%", background:"#6366F1", borderRadius:3, width:`${topFtdAff[0][1]?(ftd/topFtdAff[0][1]*100):0}%` }} />
                        </div>
                        <span style={{ fontSize:11, fontWeight:700, color:"#334155", minWidth:20, textAlign:"right" }}>{ftd}</span>
                        <span style={{ fontSize:10, color:"#64748B", maxWidth:80, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{name}</span>
                      </div>
                    ))
                  }
                </div>
                <div style={{ background:"#F8FAFC", border:"1px solid #E2E8F0", borderRadius:10, padding:"12px 14px" }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#334155", marginBottom:10 }}>🏦 Top Brokers <span style={{ fontSize:10, color:"#94A3B8" }}>by FTD</span></div>
                  {topFtdBrokers.length === 0 ? <div style={{ color:"#94A3B8", fontSize:12 }}>No data</div> :
                    topFtdBrokers.map(([name, ftd], i) => (
                      <div key={i} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:7 }}>
                        <span style={{ fontSize:11, color:"#94A3B8", minWidth:16 }}>{i+1}</span>
                        <div style={{ flex:1, height:6, background:"#E2E8F0", borderRadius:3, overflow:"hidden" }}>
                          <div style={{ height:"100%", background:"#0EA5E9", borderRadius:3, width:`${topFtdBrokers[0][1]?(ftd/topFtdBrokers[0][1]*100):0}%` }} />
                        </div>
                        <span style={{ fontSize:11, fontWeight:700, color:"#334155", minWidth:20, textAlign:"right" }}>{ftd}</span>
                        <span style={{ fontSize:10, color:"#64748B", maxWidth:80, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{name}</span>
                      </div>
                    ))
                  }
                </div>
                <div style={{ background:"#F8FAFC", border:"1px solid #E2E8F0", borderRadius:10, padding:"12px 14px" }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#334155", marginBottom:10 }}>🌍 Top Countries <span style={{ fontSize:10, color:"#94A3B8" }}>by FTD</span></div>
                  {topFtdCountries.length === 0 ? <div style={{ color:"#94A3B8", fontSize:12 }}>No data</div> :
                    topFtdCountries.map(([name, ftd], i) => (
                      <div key={i} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:7 }}>
                        <span style={{ fontSize:11, color:"#94A3B8", minWidth:16 }}>{i+1}</span>
                        <div style={{ flex:1, height:6, background:"#E2E8F0", borderRadius:3, overflow:"hidden" }}>
                          <div style={{ height:"100%", background:"#10B981", borderRadius:3, width:`${topFtdCountries[0][1]?(ftd/topFtdCountries[0][1]*100):0}%` }} />
                        </div>
                        <span style={{ fontSize:11, fontWeight:700, color:"#334155", minWidth:20, textAlign:"right" }}>{ftd}</span>
                        <span style={{ fontSize:10, color:"#64748B", maxWidth:80, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{name}</span>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>
          );
        })()}




        {/* ═══════════ CRG DEALS ═══════════ */}
        {sectionTitle("📋", "CRG Deals")}
        {(() => {
          // Today's values
          const _todayCrg = crgDeals.filter(d => d.date === today);
          const _todayCap = _todayCrg.reduce((s,d) => s+(parseInt(d.cap)||0), 0);
          const _todayStarted = _todayCrg.filter(d => d.started).length;
          // FIX: FTD comes from ftd-entries table (FTD Info page), not CRG deals
          const _todayFtdEntries = ftdEntries.filter(d => d.date === today);
          const _todayFtd = _todayFtdEntries.reduce((s,d) => s+(parseInt(d.ftd)||0), 0);
          // Yesterday FTD from ftd-entries
          const _yesterday = (() => { const d=new Date(); d.setUTCDate(d.getUTCDate()-1); return d.toISOString().split('T')[0]; })();
          const _yesterdayFtd = ftdEntries.filter(d => d.date === _yesterday).reduce((s,d) => s+(parseInt(d.ftd)||0), 0);
          // Last working day with data (most recent date before today that has entries)
          const _datesWithData = [...new Set(crgDeals.map(d=>d.date).filter(d=>d&&d<today))].sort().reverse();
          const _lastWorkDate = _datesWithData[0] || null;
          const _lastWorkCrg = _lastWorkDate ? crgDeals.filter(d => d.date === _lastWorkDate) : [];
          const _lastWorkCap = _lastWorkCrg.reduce((s,d) => s+(parseInt(d.cap)||0), 0);
          const _lastWorkDeals = _lastWorkCrg.length;
          // Total Received — monthly capReceived from CRG deals
          const _mPfxKpi = `${nowGMT2().getUTCFullYear()}-${String(nowGMT2().getUTCMonth()+1).padStart(2,"0")}`;
          const _monthAllCrg = crgDeals.filter(d => (d.date||"").startsWith(_mPfxKpi));
          const _monthTotalReceived = _monthAllCrg.reduce((s,d) => s+(parseInt(d.capReceived)||0), 0);
          const _daysWithReceived = new Set(_monthAllCrg.filter(d=>parseInt(d.capReceived)>0).map(d=>d.date)).size;
          const _avgReceivedPerDay = _daysWithReceived > 0 ? Math.round(_monthTotalReceived / _daysWithReceived) : 0;
          return (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 16 }}>
          <KpiCard label="CAP" value={_todayCap} prevValue={_lastWorkCap} icon="📊" accent="#6366F1" bg="#EEF2FF" onClick={() => onNav("crg")} />
          <KpiCard label="Deals" value={_todayCrg.length} prevValue={_lastWorkDeals} icon="📝" accent="#F59E0B" bg="linear-gradient(135deg,#FFFBEB,#FEF3C7)" onClick={() => onNav("crg")} />
          <KpiCard label="Started" value={_todayStarted} icon="🚀" accent="#10B981" bg="linear-gradient(135deg,#ECFDF5,#D1FAE5)" />
          <KpiCard label="FTD" value={_todayFtd} prevValue={_yesterdayFtd} icon="🎯" accent="#F59E0B" bg="#FFFBEB" />
          <KpiCard label="Total Received" value={_monthTotalReceived} icon="💰" accent="#0EA5E9" bg="linear-gradient(135deg,#EFF6FF,#DBEAFE)" suffix={` (${_avgReceivedPerDay}/day)`} />
        </div>
          );
        })()}

        {/* ═══ Daily Cap — Monthly Bar Chart ═══ */}
        {(() => {
          const now2 = nowGMT2();
          const monthPfx2 = `${now2.getUTCFullYear()}-${String(now2.getUTCMonth()+1).padStart(2,"0")}`;
          const daysInMonth2 = new Date(Date.UTC(now2.getUTCFullYear(), now2.getUTCMonth()+1, 0)).getUTCDate();
          const todayDay2 = now2.getUTCDate();
          const MONTHS2 = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
          const dayData2 = Array.from({length: daysInMonth2}, (_, i) => {
            const dayNum = i + 1;
            const dateStr2 = `${monthPfx2}-${String(dayNum).padStart(2,"0")}`;
            const dayEntries2 = dcEntries.filter(d => d.date === dateStr2);
            const aff2 = dayEntries2.reduce((s,d) => s+(parseInt(d.affiliates)||0), 0);
            const brands2 = dayEntries2.reduce((s,d) => s+(parseInt(d.brands)||0), 0);
            const dayCrg2 = crgDeals.filter(d => d.date === dateStr2);
            const received2 = dayCrg2.reduce((s,d) => s+(parseInt(d.capReceived)||0), 0);
            return { day: dayNum, dateStr: dateStr2, aff: aff2, brands: brands2, total: brands2, received: received2, isFuture: dayNum > todayDay2, isToday: dayNum === todayDay2 };
          });
          const maxVal2 = Math.max(...dayData2.map(d => Math.max(d.brands, d.received)), 1);
          const monthTotalBrands = dayData2.reduce((s,d) => s+d.brands, 0);
          const monthTotalReceived2 = dayData2.reduce((s,d) => s+d.received, 0);
          const daysWithData = dayData2.filter(d => d.brands > 0).length;
          const avgPerDay2 = daysWithData > 0 ? Math.round(monthTotalBrands / daysWithData) : 0;
          const todayEntry = dayData2.find(d => d.isToday);
          return (
            <div style={{ ...cardStyle, marginBottom: 16 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:"#334155" }}>📊 Daily Cap — {MONTHS2[now2.getUTCMonth()]} {now2.getUTCFullYear()}</div>
                  <span style={{ fontSize:11, color:"#94A3B8" }}>Brands per day</span>
                </div>
                <div style={{ display:"flex", gap:16, fontSize:11 }}>
                  <span style={{ display:"flex", alignItems:"center", gap:4 }}><span style={{ width:10, height:10, borderRadius:2, background:"#10B981", display:"inline-block" }} />&nbsp;Brands <b style={{color:"#10B981"}}>{monthTotalBrands}</b></span>
                  <span style={{ display:"flex", alignItems:"center", gap:4 }}><span style={{ width:10, height:10, borderRadius:2, background:"#F59E0B", display:"inline-block" }} />&nbsp;Received <b style={{color:"#F59E0B"}}>{monthTotalReceived2}</b></span>
                  <span style={{ fontSize:11, color:"#64748B" }}>Avg/day <b style={{color:"#334155"}}>{avgPerDay2}</b></span>
                </div>
              </div>
              <div style={{ display:"flex", alignItems:"flex-end", gap:2, height:120, padding:"0 2px", position:"relative" }}>
                {dayData2.map((d, i) => {
                  const affH = 0; // affiliates = same cap as brands, not stacked
                  const brandH = d.brands > 0 ? Math.max(3, (d.brands/maxVal2)*110) : 0;
                  return (
                    <div key={i} title={`${String(d.day).padStart(2,"0")}/${String(now2.getUTCMonth()+1).padStart(2,"0")}: Brands ${d.brands} | Received ${d.received}`}
                      style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"flex-end", position:"relative" }}>
                      {d.isToday && <div style={{ position:"absolute", top:-14, fontSize:9, fontWeight:800, color:"#6366F1" }}>▼</div>}
                      {(() => {
                        const recvH = d.received > 0 ? Math.max(3, (d.received/maxVal2)*110) : 0;
                        return d.brands > 0 || d.received > 0 ? (
                          <div style={{ width:"100%", position:"relative", borderRadius:"2px 2px 0 0", overflow:"hidden", height: Math.max(brandH, recvH) }}>
                            {brandH > 0 && <div style={{ position:"absolute", bottom:0, left:0, width:"100%", height:brandH, background: d.isToday?"#059669": d.isFuture?"#A7F3D0":"#10B981" }} />}
                            {recvH > 0 && <div style={{ position:"absolute", bottom:0, left:0, width:"55%", height:recvH, background: d.isToday?"#D97706": d.isFuture?"#FDE68A":"#F59E0B", borderRadius:"2px 0 0 0" }} />}
                          </div>
                        ) : (
                          <div style={{ width:"100%", height:2, background:"#F1F5F9", borderRadius:1 }} />
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
              <div style={{ display:"flex", marginTop:3, paddingLeft:2 }}>
                {dayData2.map((d, i) => (
                  <div key={i} style={{ flex:1, textAlign:"center", fontSize:8, color: d.isToday?"#6366F1":"#94A3B8", fontWeight: d.isToday?800:400 }}>
                    {(d.day===1 || d.day%5===0 || d.isToday) ? d.day : ""}
                  </div>
                ))}
              </div>
              <div style={{ display:"flex", gap:20, marginTop:10, borderTop:"1px solid #F1F5F9", paddingTop:8 }}>
                <span style={{ fontSize:11, color:"#64748B" }}>Brands total: <b style={{color:"#10B981",fontFamily:"'JetBrains Mono',monospace"}}>{monthTotalBrands}</b></span>
                <span style={{ fontSize:11, color:"#64748B" }}>Received total: <b style={{color:"#F59E0B",fontFamily:"'JetBrains Mono',monospace"}}>{monthTotalReceived2}</b></span>
                <span style={{ fontSize:11, color:"#64748B" }}>Today: <b style={{color:"#6366F1",fontFamily:"'JetBrains Mono',monospace"}}>{todayEntry?.brands||0}</b></span>
                <span style={{ fontSize:11, color:"#64748B" }}>Days: <b>{daysWithData}</b></span>
              </div>
            </div>
          );
        })()}

        {/* ═══════════ AUTO-B2026 ═══════════ */}
                  <CapCountryChart crgDeals={crgDeals} />
          {sectionTitle("🤖", "AUTO-B2026 — Bot Status")}
        <BotStatusWidget />
{/* ═══════════ AGENTS ═══════════ */}
        {sectionTitle("👥", "Agents — Monthly Summary")}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 16 }}>
          {/* Top Agents by CAP */}
          <div style={cardStyle}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#334155", marginBottom: 10 }}>🔥 Top Agents (CAP)</div>
            {topAgents.length === 0 ? <div style={{ color: "#94A3B8", fontSize: 12 }}>No data</div> :
              topAgents.map(([name, cap], i) => (
                <div key={name} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: i === 0 ? "#F59E0B" : "#64748B", width: 16 }}>{i + 1}.</span>
                  <span style={{ display: "inline-block", padding: "3px 0", background: getPersonColor(name), color: getPersonTextColor(name), fontWeight: 700, fontSize: 11, textAlign: "center", width: 60, borderRadius: 4 }}>{name}</span>
                  <div style={{ flex: 1, height: 8, background: "#F1F5F9", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: (topAgents[0][1] > 0 ? (cap / topAgents[0][1]) * 100 : 0) + "%", background: i === 0 ? "linear-gradient(90deg, #F59E0B, #FBBF24)" : "#CBD5E1", borderRadius: 4 }} />
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: "#334155", minWidth: 35, textAlign: "right" }}>{cap}</span>
                </div>
              ))
            }
          </div>
          {/* Affiliates per Agent */}
          <div style={cardStyle}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#334155", marginBottom: 10 }}>📊 Affiliates / Agent</div>
            {agentList.length === 0 ? <div style={{ color: "#94A3B8", fontSize: 12 }}>No data</div> :
              agentList.map(agent => (
                <div key={agent} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                  <span style={{ display: "inline-block", padding: "2px 0", background: getPersonColor(agent), color: getPersonTextColor(agent), fontWeight: 700, fontSize: 10, textAlign: "center", width: 55, borderRadius: 4 }}>{agent}</span>
                  <div style={{ flex: 1, height: 8, background: "#F1F5F9", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: (Math.max(...Object.values(agentAff), 1) > 0 ? ((agentAff[agent] || 0) / Math.max(...Object.values(agentAff), 1)) * 100 : 0) + "%", background: "#6366F1", borderRadius: 4 }} />
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: "#334155", minWidth: 30, textAlign: "right" }}>{(agentAff[agent] || 0)}</span>
                </div>
              ))
            }
            {agentList.length > 0 && <div style={{ borderTop: "1px solid #E2E8F0", paddingTop: 4, marginTop: 6, textAlign: "right", fontSize: 11, fontWeight: 800, color: "#6366F1" }}>Total: {Object.values(agentAff).reduce((s, v) => s + v, 0)}</div>}
          </div>
          {/* Brands per Agent */}
          <div style={cardStyle}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#334155", marginBottom: 10 }}>🏢 Brands / Agent</div>
            {brandAgentList.length === 0 ? <div style={{ color: "#94A3B8", fontSize: 12 }}>No data</div> :
              brandAgentList.map(agent => (
                <div key={agent} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                  <span style={{ display: "inline-block", padding: "2px 0", background: getPersonColor(agent), color: getPersonTextColor(agent), fontWeight: 700, fontSize: 10, textAlign: "center", width: 55, borderRadius: 4 }}>{agent}</span>
                  <div style={{ flex: 1, height: 8, background: "#F1F5F9", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: (Math.max(...Object.values(agentBrandRec), 1) > 0 ? ((agentBrandRec[agent] || 0) / Math.max(...Object.values(agentBrandRec), 1)) * 100 : 0) + "%", background: "#10B981", borderRadius: 4 }} />
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: "#334155", minWidth: 52, textAlign: "right" }}>{`${agentBrandRec[agent] || 0} / ${agentBrandPlan[agent] || 0}`}</span>
                </div>
              ))
            }
            {brandAgentList.length > 0 && <div style={{ borderTop: "1px solid #E2E8F0", paddingTop: 4, marginTop: 6, textAlign: "right", fontSize: 11, fontWeight: 800, color: "#10B981" }}>Total: {Object.values(agentBrandRec).reduce((s, v) => s + v, 0)} / {Object.values(agentBrandPlan).reduce((s, v) => s + v, 0)}</div>}
          </div>
          {/* FTDs per Agent */}
          <div style={cardStyle}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#334155", marginBottom: 10 }}>⭐ FTDs / Agent</div>
            {ftdAgentList.length === 0 ? <div style={{ color: "#94A3B8", fontSize: 12 }}>No data</div> :
              ftdAgentList.map(agent => (
                <div key={agent} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                  <span style={{ display: "inline-block", padding: "2px 0", background: getPersonColor(agent), color: getPersonTextColor(agent), fontWeight: 700, fontSize: 10, textAlign: "center", width: 55, borderRadius: 4 }}>{agent}</span>
                  <div style={{ flex: 1, height: 8, background: "#F1F5F9", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: (Math.max(...Object.values(agentFtd), 1) > 0 ? ((agentFtd[agent] || 0) / Math.max(...Object.values(agentFtd), 1)) * 100 : 0) + "%", background: "#EC4899", borderRadius: 4 }} />
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: "#334155", minWidth: 30, textAlign: "right" }}>{(agentFtd[agent] || 0)}</span>
                </div>
              ))
            }
            {ftdAgentList.length > 0 && <div style={{ borderTop: "1px solid #E2E8F0", paddingTop: 4, marginTop: 6, textAlign: "right", fontSize: 11, fontWeight: 800, color: "#EC4899" }}>Total: {Object.values(agentFtd).reduce((s, v) => s + v, 0)}</div>}
          </div>
        </div>

        {/* ═══ Agent Targets Table ═══ */}
        <AgentTargetsTable dcEntries={dcEntries} now={now} agentFtd={agentFtd} agentBrandRec={agentBrandRec} />

        {/* ═══════════ OFFERS ═══════════ */}
        {sectionTitle("🤝", "Offers — Top 8 Countries")}
        <div style={{ ...cardStyle, marginBottom: 28 }}>
          {topOfferCountries.length === 0 ? <div style={{ color: "#94A3B8", fontSize: 12 }}>No offers data</div> : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 10 }}>
              {topOfferCountries.map(([country, count], i) => {
                const maxC = topOfferCountries[0][1];
                const colors = ["#6366F1", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#0EA5E9", "#EC4899", "#14B8A6"];
                return (
                  <div key={country} style={{ textAlign: "center", padding: "12px 8px", background: "#F8FAFC", borderRadius: 10, border: "1px solid #E2E8F0" }}>
                    <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 2 }}>{country}</div>
                    <div style={{ height: 6, background: "#E2E8F0", borderRadius: 3, margin: "6px 0", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: (maxC > 0 ? (count / maxC) * 100 : 0) + "%", background: colors[i] || "#CBD5E1", borderRadius: 3 }} />
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: colors[i] || "#334155", fontFamily: "'JetBrains Mono',monospace" }}>{count}</div>
                    <div style={{ fontSize: 9, color: "#94A3B8", fontWeight: 600 }}>offers</div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ marginTop: 10, textAlign: "right", fontSize: 11, color: "#64748B" }}>Total: <b>{offers.length}</b> offers across <b>{Object.keys(offerCountryMap).length}</b> countries</div>
        </div>

        {/* ═══════════ PARTNERS ═══════════ */}
        {(() => {
          const monthPfx = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
          const monthPartners = partnersAll.filter(p => (p.date || "").startsWith(monthPfx));
          const partnerAgentMap = {};
          monthPartners.forEach(p => {
            const a = normalizeAgent((p.openBy || p.agent || "").trim());
            if (a) partnerAgentMap[a] = (partnerAgentMap[a] || 0) + 1;
          });
          const topPartnerAgents = Object.entries(partnerAgentMap).sort((a, b) => b[1] - a[1]);
          const typeBreakdown = {};
          monthPartners.forEach(p => { typeBreakdown[p.type || "?"] = (typeBreakdown[p.type || "?"] || 0) + 1; });

          return (
            <>
              {sectionTitle("🤝", `Partners — ${MONTHS[now.getUTCMonth()]} ${now.getUTCFullYear()}`)}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 28 }}>
                <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 14, padding: "16px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>🏆 Partnerships per Agent</div>
                    <span style={{ fontSize: 22, fontWeight: 800, color: "#EC4899", fontFamily: "'JetBrains Mono',monospace" }}>{monthPartners.length}</span>
                  </div>
                  {topPartnerAgents.length === 0 ? <div style={{ color: "#94A3B8", fontSize: 12 }}>No partners this month</div> :
                    topPartnerAgents.map(([name, count], i) => (
                      <div key={name} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: i === 0 ? "#F59E0B" : "#64748B", width: 20 }}>#{i + 1}</span>
                        <span style={{ display: "inline-block", padding: "3px 0", background: getPersonColor(name), color: getPersonTextColor(name), fontWeight: 700, fontSize: 11, textAlign: "center", width: 60, borderRadius: 4 }}>{name}</span>
                        <div style={{ flex: 1, height: 6, background: "#F1F5F9", borderRadius: 5, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: (topPartnerAgents[0][1] > 0 ? (count / topPartnerAgents[0][1]) * 100 : 0) + "%", background: i === 0 ? "linear-gradient(90deg, #EC4899, #F472B6)" : "#CBD5E1", borderRadius: 5 }} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "#0F172A", fontFamily: "'JetBrains Mono',monospace", minWidth: 25, textAlign: "right" }}>{count}</span>
                      </div>
                    ))
                  }
                </div>
                <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 14, padding: "16px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 12 }}>📊 Type Breakdown</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {[["Brand", "#6366F1"], ["Network", "#0EA5E9"], ["Affiliate", "#10B981"]].map(([type, color]) => (
                      <div key={type} style={{ flex: 1, minWidth: 90, textAlign: "center", padding: "14px 8px", background: color + "08", borderRadius: 12, border: `1px solid ${color}20` }}>
                        <div style={{ fontSize: 28, fontWeight: 800, color, fontFamily: "'JetBrains Mono',monospace" }}>{typeBreakdown[type] || 0}</div>
                        <div style={{ fontSize: 11, color: "#64748B", fontWeight: 600, marginTop: 2 }}>{type}s</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          );
        })()}

        

                {/* ═══════════ BLITZ RECORDS — Top of Dashboard ═══════════ */}
        <BlitzRecords />
      </main>
    </div>


  );
}
